// packages/report-components/src/SectionIntro.tsx
//
// Short narrative paragraph between a section heading and its data. Exists
// purely to enforce consistent typography + spacing — the LLM was producing
// loose <p> tags with inconsistent margins before.
//
// Measure: `--verity-measure-prose`, the same token the exec-summary
// paragraph and the header subtitle use. It replaced a hard `720px`, which
// was a fourth distinct measure and made this paragraph end at its own
// x-position on the page.

import * as React from "react";

import { usePageDensity } from "./PageDensityContext";
import { pageLines, useTruncatedFlag } from "./pageClamp";

export type SectionIntroProps = {
  text: string;
  /** How many lines the server budgeted for this paragraph ON A PAGE (the
   * adapter passes the allocation's count). The paragraph is clamped to it,
   * so what the cell was sized for is what is drawn. Ignored off a page. */
  lines?: number;
  /** The type this paragraph is set in ON A PAGE, when it is not body prose.
   * The `standfirst` analysis kind is a step up (plan 2026-09-27-page-two-
   * looks-like-the-drawing): it breaks earlier and reads as a standfirst
   * rather than as the first paragraph.
   *
   * PROPS and not a density value, because the size belongs to the KIND and
   * not to the page: a `narrative` block drawing a `SectionIntro` on the same
   * page keeps body type. Absent is exactly what every existing report
   * passes, and falls back to the page's prose. */
  fontPx?: number;
  linePx?: number;
};

export function SectionIntro({
  text,
  lines,
  fontPx,
  linePx,
}: SectionIntroProps): React.ReactElement | null {
  const density = usePageDensity();
  const truncated = useTruncatedFlag<HTMLParagraphElement>(`${text}|${lines}`);
  if (typeof text !== "string" || !text.trim()) {
    return null;
  }
  if (density) {
    // A page cell: no margins (the cell IS the box), the page's prose type on
    // its fixed line, the full width the budget counted characters over —
    // unless the block's KIND set its own, which is what a standfirst does.
    // Both or neither: a size without its line height would set a larger face
    // on the leading the server priced, and the paragraph would overflow the
    // cell it was budgeted for.
    const size = fontPx && fontPx > 0 ? fontPx : density.prose_font_px;
    const line = fontPx && fontPx > 0 && linePx && linePx > 0 ? linePx : density.prose_line_px;
    return (
      <p
        ref={truncated}
        className="verity-section-intro"
        data-page-prose="1"
        style={{
          margin: 0,
          fontSize: size,
          color: "var(--verity-color-text-muted, #6b7280)",
          ...(lines && lines > 0
            ? pageLines(lines, line, { fixed: false })
            : { lineHeight: `${line}px` }),
        }}
      >
        {text}
      </p>
    );
  }
  return (
    <p
      className="verity-section-intro"
      style={{
        marginTop: "var(--verity-space-sm, 8px)",
        marginBottom: "var(--verity-space-md, 16px)",
        fontSize: "var(--verity-font-size-md, 15px)",
        lineHeight: "var(--verity-line-height-relaxed, 1.7)",
        color: "var(--verity-color-text-muted, #6b7280)",
        maxWidth: "var(--verity-measure-prose, 72ch)",
      }}
    >
      {text}
    </p>
  );
}
